<template>
  <Modal :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" size="7xl">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-file-alt text-primary-600 dark:text-primary-400"></i>
        <span>Quotation Preview: {{ quotation ? quotation.quotation_number : 'Loading...' }}</span>
      </div>
    </template>

    <template #content>
      <!-- Loading state -->
      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-blue-500 text-3xl mb-4"></i>
          <p class="text-gray-600 dark:text-dark-400">Loading quotation details...</p>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error"
        class="bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-300 px-4 py-3 rounded-md mb-6">
        <p>{{ error }}</p>
        <button @click="fetchQuotation"
          class="mt-2 text-sm text-error-700 dark:text-error-300 underline hover:text-error-800 dark:hover:text-error-200">
          Try again
        </button>
      </div>

      <!-- Quotation details -->
      <div v-else-if="quotation" class="space-y-6">
        <!-- Client and Quotation Info -->
        <div class="bg-white dark:bg-dark-800 rounded-lg p-6 border border-gray-200 dark:border-dark-700">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4">Client Information</h3>
              <div class="space-y-2">
                <p v-if="quotation.contact_person"><span class="font-medium">Name:</span> {{ quotation.contact_person }}
                </p>
                <p v-else-if="quotation.first_name || quotation.last_name"><span class="font-medium">Name:</span> {{
                  quotation.first_name || '' }} {{ quotation.last_name || '' }}</p>
                <p v-if="quotation.company_name"><span class="font-medium">Company:</span> {{ quotation.company_name }}
                </p>
                <p><span class="font-medium">Email:</span> {{ quotation.email || quotation.client_email || 'N/A' }}</p>
                <p><span class="font-medium">Phone:</span> {{ quotation.phone || quotation.client_phone ||
                  quotation.contact_number || 'N/A' }}</p>
                <p><span class="font-medium">Address:</span> {{ quotation.address || quotation.client_address || 'N/A'
                  }}</p>
              </div>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4">Quotation Information</h3>
              <div class="space-y-2">
                <p><span class="font-medium">Quotation #:</span> {{ quotation.quotation_number }}</p>
                <p><span class="font-medium">Date:</span> {{ formatDate(quotation.quote_date) }}</p>
                <p><span class="font-medium">Valid Until:</span> {{ formatDate(quotation.valid_until) }}</p>
                <p>
                  <span class="font-medium">Status:</span>
                  <span :class="getStatusClass(quotation.status)" class="px-2 py-1 text-xs rounded-full">
                    {{ formatStatus(quotation.status) }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <div class="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-white/10 dark:border-white/5">
            <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100">Items</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
              <thead class="bg-gray-50 dark:bg-dark-700">
                <tr>
                  <th scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount (Per Unit)
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Discount
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pre-Discount
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Price
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
                <tr v-for="(item, index) in quotation.items" :key="index">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-100">
                    {{ item.description }}
                    <div
                      v-if="item.width && item.height && item.dimension_unit && !isNaN(parseFloat(item.width)) && !isNaN(parseFloat(item.height))"
                      class="text-xs text-gray-500 dark:text-dark-400 mt-1">
                      {{ parseFloat(item.width).toFixed(2) }} {{ item.dimension_unit }} by {{
                        parseFloat(item.height).toFixed(2) }} {{ item.dimension_unit }}
                    </div>
                    <div v-if="item.unit_of_measure" class="text-xs text-gray-500 dark:text-dark-400">
                      Unit: {{ item.unit_of_measure }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-400 text-center">
                    {{ item.quantity }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-400 text-right">
                    {{ formatCurrency(item.unit_price) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-400 text-center">
                    <span v-if="item.discount_type === 'none'">None</span>
                    <span v-else-if="item.discount_type === 'percentage'">{{ item.discount_value }}%</span>
                    <span v-else-if="item.discount_type === 'fixed'">{{ formatCurrency(item.discount_value) }}</span>
                  </td>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 text-right font-medium">
                    {{ formatCurrency(calculateItemTotalDiscount(item)) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-400 text-right">
                    {{ formatCurrency(item.pre_discount_subtotal) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-right"
                    :class="item.discount_type && item.discount_type !== 'none' && item.discount_value > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-dark-100'">
                    {{ formatCurrency(calculateItemTotalPrice(item)) }}
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-50 dark:bg-dark-700">
                <!-- Pre-discount total -->
                <tr>
                  <td colspan="5"
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-dark-100 text-right">
                    Pre-Discount:
                  </td>
                  <td colspan="2"
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-dark-100 text-right">
                    {{ formatCurrency(calculatePreDiscountSubtotal(quotation)) }}
                  </td>
                </tr>

                <!-- Total discounts (combined) -->
                <tr v-if="(calculateTotalItemDiscounts(quotation) + getOverallDiscountAmount(quotation)) > 0">
                  <td colspan="5"
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-surface-900 dark:text-surface-100 text-right">
                    Less: Total Discounts
                  </td>
                  <td colspan="2"
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-success-600 dark:text-success-400 text-right">
                    -{{ formatCurrency((calculateTotalItemDiscounts(quotation) + getOverallDiscountAmount(quotation)))
                    }}
                  </td>
                </tr>



                <tr v-if="quotation?.total_discount_type && quotation.total_discount_type !== 'none'"
                  class="text-xs text-gray-500 dark:text-dark-400">
                  <td colspan="5" class="px-6 py-2 text-right italic">
                    <span v-if="quotation.total_discount_type === 'percentage'">
                      Overall {{ quotation.total_discount_value }}% discount
                    </span>
                    <span v-else>
                      Overall fixed discount
                    </span>
                  </td>
                  <td colspan="2" class="px-6 py-2 text-right italic">
                    -{{ formatCurrency(getOverallDiscountAmount(quotation)) }}
                  </td>
                </tr>

                <!-- Divider -->
                <tr class="border-t-2 border-gray-300 dark:border-dark-500">
                  <td colspan="7" class="py-1"></td>
                </tr>

                <!-- Final total -->
                <tr class="font-bold text-lg">
                  <td colspan="5"
                    class="px-6 py-4 whitespace-nowrap text-base font-bold text-surface-900 dark:text-surface-100 text-right">
                    TOTAL AMOUNT:
                  </td>
                  <td colspan="2"
                    class="px-6 py-4 whitespace-nowrap text-base font-bold text-surface-900 dark:text-surface-100 text-right">
                    {{ formatCurrency(calculatePreDiscountSubtotal(quotation) - (calculateTotalItemDiscounts(quotation)
                      + getOverallDiscountAmount(quotation))) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Notes -->
        <div v-if="quotation.notes" class="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 p-6">
          <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4">Notes</h3>
          <p class="text-gray-700 dark:text-dark-300 whitespace-pre-line">{{ quotation.notes }}</p>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-between">
        <button @click="$emit('update:modelValue', false)" class="btn btn-ghost">
          Close
        </button>
        <div class="flex items-center gap-2">
          <button
            v-if="quotation && (['system_approved', 'client_approved', 'approved'].includes(quotation.status)) && !hidePdfButton"
            @click="emailQuotation" :disabled="emailing || !canEmailQuotation"
            class="btn btn-primary bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            title="Send quotation to client's email">
            <i class="fas fa-envelope mr-2"></i> Email Quotation
          </button>
          <button
            v-if="quotation && (['system_approved', 'client_approved', 'approved'].includes(quotation.status)) && !hidePdfButton"
            @click="generatePDF" class="btn btn-primary">
            <i class="fas fa-file-pdf mr-2"></i> Generate PDF
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { format, parseISO } from 'date-fns';
import { useToast } from '@/composables/useToast';
import { quotationsApi } from '@/services/api';
import Modal from '@/components/common/Modal.vue';
import { generateQuotationPDF } from '@/utils/clientPdfGenerator';
import { useApi } from '@/composables/useApi';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  quotationId: {
    type: [Number, String],
    required: true
  },
  hidePdfButton: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue']);

const toast = useToast();
const api = useApi();
const quotation = ref(null);
const loading = ref(true);
const error = ref(null);
const emailing = ref(false);

watch(() => props.modelValue, (newVal) => {
  if (newVal && props.quotationId) {
    fetchQuotation();
  }
});

watch(() => props.quotationId, (newVal) => {
  if (props.modelValue && newVal) {
    fetchQuotation();
  }
});

onMounted(() => {
  if (props.modelValue && props.quotationId) {
    fetchQuotation();
  }
});

const fetchQuotation = async () => {
  loading.value = true;
  error.value = null;

  try {
    const response = await quotationsApi.getById(props.quotationId);
    quotation.value = response.data.data;
    console.log('Quotation details:', quotation.value);
    console.log('Discount info:', {
      type: quotation.value.total_discount_type,
      value: quotation.value.total_discount_value,
      amount: quotation.value.total_discount
    });
  } catch (err) {
    console.error('Error fetching quotation:', err);
    error.value = 'Failed to load quotation details. Please try again.';
    toast.error('Failed to load quotation details');
  } finally {
    loading.value = false;
  }
};

const generatePDF = async () => {
  try {
    // Add a loading toast that can be updated
    const toastId = toast.info('Generating PDF...', {
      timeout: false,
      closeButton: false
    });

    // We already have the quotation data in the quotation.value ref
    if (!quotation.value) {
      throw new Error('Quotation data not available');
    }

    // Generate PDF on the client side
    console.log('Generating client-side PDF for quotation:', quotation.value.quotation_number);
    const pdfBlob = await generateQuotationPDF(quotation.value);

    // Create a URL for the blob
    const url = window.URL.createObjectURL(pdfBlob);

    // Close the loading toast
    toast.dismiss(toastId);
    toast.success('PDF generated successfully');

    // Create a temporary link element to force download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Quotation-${quotation.value?.quotation_number || 'download'}.pdf`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 1000);
  } catch (err) {
    console.error('Error generating PDF:', err);
    toast.error(`Failed to generate PDF: ${err.message || 'Unknown error'}`);
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return format(new Date(dateString), 'MMMM d, yyyy');
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP'
  }).format(value || 0);
};

const formatStatus = (status) => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getStatusClass = (status) => {
  const statusClasses = {
    'draft': 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300',
    'pending': 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300',
    'accepted': 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
    'rejected': 'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300',
    'expired': 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300'
  };

  return statusClasses[status] || 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300';
};

// Email eligibility (allow send if valid email; honor allow_email_updates when present)
const canEmailQuotation = computed(() => {
  const email = quotation.value?.email || quotation.value?.client_email || '';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const allowed = quotation.value?.allow_email_updates !== undefined ? quotation.value.allow_email_updates === true : true;
  return !!email && emailRegex.test(email) && allowed;
});

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const emailQuotation = async () => {
  try {
    if (!quotation.value) {
      toast.error('Quotation not loaded');
      return;
    }
    if (!canEmailQuotation.value) {
      toast.error('Client email invalid or not opted-in for email updates');
      return;
    }
    emailing.value = true;

    // Generate PDF and convert to base64
    const pdfBlob = await generateQuotationPDF(quotation.value);
    const pdfBase64 = await blobToBase64(pdfBlob);

    const to = (quotation.value.email || quotation.value.client_email || '').trim();
    const customerName = (quotation.value.company_name || quotation.value.contact_person || `${quotation.value.first_name || ''} ${quotation.value.last_name || ''}` || 'Customer').trim();
    const subject = `Quotation for ${quotation.value?.quotation_number || 'your request'}`;
    const formatLocal = (d) => new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const totalAmount = quotation.value?.total_amount || 0;
    const currency = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const textIntro = `Thank you for considering IGW Digital Printing Services as your trusted printing partner. We appreciate the opportunity to submit this quotation for your review.`;

    const text = `Dear ${customerName},\n\n${textIntro}\n\nKey information:\n\n- Quotation Number: ${quotation.value?.quotation_number}\n- Date Issued: ${formatLocal(quotation.value?.quote_date)}\n- Valid Until: ${formatLocal(quotation.value?.valid_until)}\n- Total Amount: ${currency(totalAmount)}\n\nYour quotation is attached as a PDF for your review. If you have any questions or need adjustments, contact us at sales@igwprints.com or 0927 426 2634 / (082) 308 0310.\n\nWe look forward to the opportunity to work with you and hope this proposal meets your requirements.\n\nBest regards,\nIGW Digital Printing Services`;

    const html = `
      <p>Dear ${customerName},</p>
      <p>Please find your quotation details below. Key information:</p>
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="width:100%;max-width:560px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;margin:8px 0 16px;">
        <tr><td style="padding:6px 8px;"><strong>Quotation Number:</strong></td><td style="padding:6px 8px;text-align:right;">${quotation.value?.quotation_number}</td></tr>
        <tr><td style="padding:6px 8px;"><strong>Date Issued:</strong></td><td style="padding:6px 8px;text-align:right;">${formatLocal(quotation.value?.quote_date)}</td></tr>
        <tr><td style="padding:6px 8px;"><strong>Valid Until:</strong></td><td style="padding:6px 8px;text-align:right;">${formatLocal(quotation.value?.valid_until)}</td></tr>
        <tr><td style="padding:6px 8px;"><strong>Total Amount:</strong></td><td style="padding:6px 8px;text-align:right;">${currency(totalAmount)}</td></tr>
      </table>
      <p>Your quotation is attached to this email as a PDF file for your review.</p>
      <p style="margin-top:16px;">If you have any questions or need adjustments, please contact our support team at <a href="mailto:sales@igwprints.com">sales@igwprints.com</a> or 0927 426 2634 / (082) 308 0310.</p>
      <p>We look forward to the opportunity to work with you and hope this proposal meets your requirements.</p>
      <p>Best regards,<br/>IGW Digital Printing Services</p>
    `;

    await api.post('/exports/email/invoice', {
      to,
      subject,
      text,
      html,
      pdfBase64,
      filename: `quotation-${quotation.value?.quotation_number || 'details'}.pdf`
    });

    toast.success('Quotation emailed successfully');
  } catch (err) {
    console.error('Error emailing quotation:', err);
    toast.error('Failed to email quotation');
  } finally {
    emailing.value = false;
  }
};

// Add helper functions for discount calculations
const hasItemDiscounts = (quotation) => {
  if (!quotation?.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
    return false;
  }

  return quotation.items.some(item =>
    item.discount_type &&
    item.discount_type !== 'none' &&
    item.discount_value &&
    parseFloat(item.discount_value) > 0
  );
};

// Calculate true pre-discount subtotal by summing pre_discount_subtotal values
const calculatePreDiscountSubtotal = (quotation) => {
  if (!quotation?.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
    return 0;
  }

  return quotation.items.reduce((total, item) => {
    return total + parseFloat(item.pre_discount_subtotal || 0);
  }, 0);
};

// Calculate the correct discounted price per item
const calculateItemTotalPrice = (item) => {
  const preDiscountSubtotal = parseFloat(item.pre_discount_subtotal || 0);

  if (!item.discount_type || item.discount_type === 'none' || !item.discount_value) {
    return preDiscountSubtotal;
  }

  let discountAmount = 0;
  const quantity = parseFloat(item.quantity || 1);
  const unitPrice = parseFloat(item.unit_price || 0);

  if (item.discount_type === 'percentage') {
    // Percentage discount applied to unit price
    const unitDiscountAmount = (unitPrice * parseFloat(item.discount_value)) / 100;
    discountAmount = unitDiscountAmount * quantity;
    if (item.is_dimensions && item.width && item.height) {
      // For dimensional items, multiply by area
      discountAmount = discountAmount * (item.width * item.height);
    }
  } else if (item.discount_type === 'fixed') {
    // Fixed discount per unit
    discountAmount = parseFloat(item.discount_value) * quantity;
    if (item.is_dimensions && item.width && item.height) {
      // For dimensional items, multiply by area
      discountAmount = discountAmount * (item.width * item.height);
    }
    // Ensure discount doesn't exceed the subtotal
    discountAmount = Math.min(discountAmount, preDiscountSubtotal);
  }

  return Math.max(preDiscountSubtotal - discountAmount, 0);
};

const calculateItemTotalDiscount = (item) => {
  if (!item.discount_type || item.discount_type === 'none' || !item.discount_value || item.discount_value <= 0) {
    return 0;
  }

  const quantity = parseFloat(item.quantity || 1);
  const unitPrice = parseFloat(item.unit_price || 0);
  let totalDiscount = 0;

  if (item.discount_type === 'percentage') {
    // Percentage discount applied to unit price
    const unitDiscountAmount = (unitPrice * parseFloat(item.discount_value)) / 100;
    totalDiscount = unitDiscountAmount * quantity;
    if (item.is_dimensions && item.width && item.height) {
      // For dimensional items, multiply by area
      totalDiscount = totalDiscount * (item.width * item.height);
    }
  } else if (item.discount_type === 'fixed') {
    // Fixed discount per unit
    totalDiscount = parseFloat(item.discount_value) * quantity;
    if (item.is_dimensions && item.width && item.height) {
      // For dimensional items, multiply by area
      totalDiscount = totalDiscount * (item.width * item.height);
    }
    // Ensure discount doesn't exceed the subtotal
    const preDiscountSubtotal = parseFloat(item.pre_discount_subtotal || 0);
    totalDiscount = Math.min(totalDiscount, preDiscountSubtotal);
  }

  return totalDiscount;
};

const calculateTotalItemDiscounts = (quotation) => {
  if (!quotation?.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
    return 0;
  }

  return quotation.items.reduce((total, item) => {
    if (!item.discount_type || item.discount_type === 'none' || !item.discount_value) {
      return total;
    }

    const quantity = parseFloat(item.quantity || 1);
    const unitPrice = parseFloat(item.unit_price || 0);
    let discountAmount = 0;

    if (item.discount_type === 'percentage') {
      // Percentage discount applied to unit price
      const unitDiscountAmount = (unitPrice * parseFloat(item.discount_value)) / 100;
      discountAmount = unitDiscountAmount * quantity;
      if (item.is_dimensions && item.width && item.height) {
        // For dimensional items, multiply by area
        discountAmount = discountAmount * (item.width * item.height);
      }
    } else if (item.discount_type === 'fixed') {
      // Fixed discount per unit
      discountAmount = parseFloat(item.discount_value) * quantity;
      if (item.is_dimensions && item.width && item.height) {
        // For dimensional items, multiply by area
        discountAmount = discountAmount * (item.width * item.height);
      }
      // Ensure discount doesn't exceed the subtotal
      const preDiscountSubtotal = parseFloat(item.pre_discount_subtotal || 0);
      discountAmount = Math.min(discountAmount, preDiscountSubtotal);
    }

    return total + discountAmount;
  }, 0);
};

const getItemDiscountSummary = (quotation) => {
  if (!quotation?.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
    return 'No item discounts';
  }

  const discountedItems = quotation.items.filter(item =>
    item.discount_type &&
    item.discount_type !== 'none' &&
    item.discount_value &&
    parseFloat(item.discount_value) > 0
  );

  if (discountedItems.length === 0) {
    return 'No item discounts';
  }

  const fixedItems = discountedItems.filter(item => item.discount_type === 'fixed');
  const percentageItems = discountedItems.filter(item => item.discount_type === 'percentage');

  const parts = [];

  if (fixedItems.length > 0) {
    parts.push(`${fixedItems.length} item${fixedItems.length > 1 ? 's' : ''} with fixed discount`);
  }

  if (percentageItems.length > 0) {
    const uniquePercentages = [...new Set(percentageItems.map(item => item.discount_value))];
    parts.push(`${percentageItems.length} item${percentageItems.length > 1 ? 's' : ''} with percentage discount${uniquePercentages.length > 1 ? 's' : ''} (${uniquePercentages.join('%, ')}%)`);
  }

  return parts.join(' and ');
};

const getOverallDiscountAmount = (quotation) => {
  if (!quotation?.total_discount_type || quotation.total_discount_type === 'none') return 0;
  if (quotation.total_discount_type === 'percentage') {
    const amount = (parseFloat(quotation.subtotal_amount) * parseFloat(quotation.total_discount_value || 0)) / 100;
    console.log('Calculated percentage discount:', amount);
    return amount;
  }
  // fixed
  const amount = parseFloat(quotation.total_discount_value || 0);
  console.log('Calculated fixed discount:', amount);
  return amount;
};
</script>